import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../services/supabase';
import { MOCK_BLOG_POSTS_RAW, TABLE_NAMES } from '../constants';
import { getLastUpdated, sortByLastUpdated } from '../utils/journalPosts';

export const useBlogPosts = () => {
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = async () => {
    console.log('[JOURNAL] Fetching journal entries from Supabase...');
    try {
      const { data, error } = await supabase
        .from(TABLE_NAMES.BLOG_POSTS)
        .select('*')
        .order('updated_at', { ascending: false, nullsFirst: false });

      if (error) {
        console.error("[JOURNAL] Error fetching journal entries from Supabase:", error);
        console.log("[JOURNAL] Falling back to mock data");
        setAllPosts(sortByLastUpdated(MOCK_BLOG_POSTS_RAW));
      } else if (data && data.length > 0) {
        console.log(`[JOURNAL] Successfully fetched ${data.length} journal entries`);
        setAllPosts(sortByLastUpdated(data));
      } else {
        console.warn("[JOURNAL] Supabase blog_posts table is empty. Using mock data.");
        setAllPosts(sortByLastUpdated(MOCK_BLOG_POSTS_RAW));
      }
    } catch (error) {
      console.error("[JOURNAL] Error in fetchPosts:", error);
      setAllPosts(sortByLastUpdated(MOCK_BLOG_POSTS_RAW));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const { latestPost, priorPostsRaw, allTags, allYears } = useMemo(() => {
    if (allPosts.length === 0) {
      return { latestPost: null, priorPostsRaw: [], allTags: [], allYears: [] };
    }

    const latest = allPosts[0];
    const prior = allPosts.slice(1);

    const tags = [...new Set(allPosts.flatMap(p => p.tags || []))].filter(Boolean).sort();
    const years = [...new Set(allPosts.map(p => new Date(getLastUpdated(p)).getFullYear()))].sort((a, b) => b - a);

    return { latestPost: latest, priorPostsRaw: prior, allTags: tags, allYears: years };
  }, [allPosts]);

  return { 
    allPosts, 
    latestPost, 
    priorPostsRaw, 
    allTags, 
    allYears, 
    loading,
    refreshPosts: fetchPosts
  };
};
